<script lang="ts">

import { Component, mixins, Prop } from "vue-facing-decorator";
import SpinnerComponent from "@/components/atoms/Spinner.vue";
import TextInputComponent from "@/components/atoms/form/TextInput.vue";
import IconComponent from "@/components/atoms/Icon.vue";
import ModalComponent from "@/components/search/Modal.vue"
import { BreakpointHandler } from "@/mixins/BreakpointHandler.mixin";
import { DateTimeZone, SearchResponseFilters } from "webshop-api";
import TabsComponent from "@/components/search/Tabs.vue";
import { useSearchResultsStore } from "@/domain/search/application/SearchResults.store";
import { DateTimeUtil, IconName, LanguageCode, TranslationKey, TranslationService } from "webshop-modules";
import { useAppliedFiltersStore } from "@/domain/search/application/AppliedFilters.store";
import { SearchAppData } from "@/domain/search/entitites/TranslationsData.interface";
import { ITab } from "@/domain/search/entitites/Tab.interface";
import { Tab } from "@/domain/search/entitites/Tab.enum";
import VueMultiselect from "vue-multiselect/src/Multiselect.vue";
import { Ref, ref } from "@vue/reactivity";
import VCalendar from "v-calendar";
import CalendarComponent from "@/app/calendar/Calendar.vue";
import { AppliedFilters } from "@/domain/search/entitites/applied-filters.interface";

interface CheckboxFilters {
    country?: string[];
    city?: string[];
    category?: number[];
    mainCategory?: number[];
}

@Component({
    components: {
        CalendarComponent,
        SpinnerComponent,
        TextInputComponent,
        IconComponent,
        ModalComponent,
        TabsComponent,
        VueMultiselect,
        VCalendar
    }
})

export default class FilterComponent extends mixins(BreakpointHandler) {

    @Prop()
    public citiesLabel!: string;

    @Prop()
    public data!: SearchAppData;

    public readonly IconName: typeof IconName = IconName;
    public readonly Translation: typeof TranslationKey = TranslationKey;
    public readonly Tab: typeof Tab = Tab;

    public searchResultsStore = useSearchResultsStore();
    public appliedFiltersStore = useAppliedFiltersStore();

    public lang: LanguageCode = TranslationService.getLanguageCode();
    public isModalVisible = false;
    public modalKey = 0;
    public modifier = '-searchFilters';
    public initialTab = Tab.Date;
    public activeTab: Tab = this.initialTab
    public tabs: Tab[] = [Tab.Date, Tab.Category, Tab.Country, Tab.City];
    public isMobile = !window.matchMedia('(min-width: 640px)').matches;
    public tabPristine = true;
    public selectedFilters: Ref<CheckboxFilters> = ref({
        country: [],
        city: [],
        category: [],
        mainCategory: [],
    });

    public mounted() {
        this.activeTab = this.initialTab;
        this.setupCalendarValueHandling();
    }

    public get filters(): SearchResponseFilters | null {
        if (!this.searchResultsStore.resultList || !this.searchResultsStore.resultList.filters) {
            return null;
        }

        return this.searchResultsStore.resultList.filters as SearchResponseFilters;
    }

    public get isLoading(): boolean {
        return this.searchResultsStore.inProgress;
    }

    /** Icon type change for checkboxes **/
    public checkboxIcon(checked: boolean): IconName {
        return checked ? IconName.Checked : IconName.Unchecked;
    }

    // add AppliedFilters to filters to avoid an empty list when filtering is too strict
    get allFilters() {
        return ['country', 'city', 'category', 'mainCategory']
            .map(filterName => this.filters && filterName in this.filters ?
                {
                    [filterName]: [...new Set([...(this.filters as SearchResponseFilters)[filterName],
                        ...(Array.isArray(this.appliedFiltersStore.$state[filterName]) ? this.appliedFiltersStore.$state[filterName] : [])
                    ])
                    ]
                }
                : [])
            .reduce((acc, o) => Object.assign(acc, o), {});
    }

    get dateStartForPicker() {
        return this.appliedFiltersStore.$state.dateStart ?? undefined;
    }

    get dateEndForPicker() {
        return this.appliedFiltersStore.$state.dateEnd ?? undefined;
    }

    public getTabConfiguration(): ITab[] {
        return this.tabs.map(tabName => ({
            name: tabName,
            translationObject: this.translationForTabName(tabName),
        }));
    }

    // for datepicker
    public selectedDates: { start: any, end: any } = {
        start: this.appliedFiltersStore.$state.dateStart
            ? this.dateStartForPicker
            : null,
        end: this.appliedFiltersStore.$state.dateEnd
            ? this.dateEndForPicker
            : null
    };

    public dateStart = '';
    public dateEnd = '';

    public updateSelectedDatesFromStore() {
        this.selectedDates = {
            start: this.appliedFiltersStore.$state.dateStart
                ? this.dateStartForPicker
                : null,
            end: this.appliedFiltersStore.$state.dateEnd
                ? this.dateEndForPicker
                : null
        }
    }

    public setDateStart() {
        const minDate = this.filters?.minDate as unknown as DateTimeZone;

        if (!minDate) {
            return;
        }

        this.dateStart = this.selectedDates.start === null
            ? ''
            : DateTimeUtil.getDateTimeNumericFormatFor(this.lang, minDate.timezone)
                .format(this.selectedDates.start.getTime() - (this.selectedDates.start.getTimezoneOffset() * 60000));

    }

    public setDateEnd() {
        const minDate = this.filters?.minDate as unknown as DateTimeZone;

        if (!minDate) {
            return;
        }

        this.dateEnd = this.selectedDates.end === null
            ? ''
            : DateTimeUtil.getDateTimeNumericFormatFor(this.lang, minDate.timezone)
                .format(this.selectedDates.end.getTime() - (this.selectedDates.end.getTimezoneOffset() * 60000));
    }

    public hideDatepickerStart = false;

    get hideDatepickerEnd() {
        return !this.hideDatepickerStart;
    }

    public updateDateStart() {
        this.setDateStart();
        this.tabPristine = false;
        const date = new Date(this.selectedDates.start!.getTime() - (this.selectedDates.start!.getTimezoneOffset() * 60000));
        this.appliedFiltersStore.addAppliedFilters({dateStart: date});
    }

    public updateDateEnd() {
        if (!this.selectedDates.end) {
            return;
        }

        this.setDateEnd();
        this.tabPristine = false;
        const date = new Date(this.selectedDates.end!.getTime() - (this.selectedDates.end!.getTimezoneOffset() * 60000));
        this.appliedFiltersStore.addAppliedFilters({dateEnd: date});
    }

    public clearDateStart() {
        this.tabPristine = false;
        this.dateStart = '';
        this.selectedDates.start = null;
        this.appliedFiltersStore.removeAppliedFiltersFromState({dateStart: undefined});
    }

    public clearDateEnd() {
        this.tabPristine = false;
        this.dateEnd = '';
        this.selectedDates.end = null;
        this.appliedFiltersStore.removeAppliedFiltersFromState({dateEnd: undefined});
    }

    public async clearDates() {
        this.dateStart = '';
        this.dateEnd = '';
        this.selectedDates.start = null;
        this.selectedDates.end = null;

        await this.appliedFiltersStore.removeAppliedFiltersFromState({dateStart: undefined, dateEnd: undefined});
        this.searchResultsStore.getData(false, this.$apiService);
    }

    public showModal() {
        this.updateSelectedDatesFromStore();
        this.setDateStart();
        this.setDateEnd();
        this.mountCalendar(true);

        this.isModalVisible = true;
        document.documentElement.style.overflowY = 'hidden';
    }

    public closeModal() {
        this.isModalVisible = false;
        document.documentElement.style.overflowY = 'visible';
    }

    public closeAndSave() {
        this.isModalVisible = false;
        document.documentElement.style.overflowY = 'visible';
        this.setDateStart();
        this.setDateEnd();
        this.applyNewFilters();
    }

    public async applyNewFilters() {
        const filterBy: AppliedFilters = {...this.selectedFilters.value};

        if (this.selectedDates.start) {
            filterBy.dateStart = this.selectedDates.start;
        }

        if (this.selectedDates.end) {
            filterBy.dateEnd = this.selectedDates.end;
        }

        await this.appliedFiltersStore.updateAppliedFilters(filterBy);
        this.searchResultsStore.getData(false, this.$apiService);
        this.tabPristine = true;
    }

    public tabSwitched() {
        if (!this.tabPristine) {
            this.applyNewFilters();
        }

        this.tabPristine = true;
    }

    public translationForTabName(tab: Tab | null) {
        switch (tab) {
            case Tab.Category:
                return {key: this.Translation.Filter_category};
            case Tab.City:
                return {key: this.Translation.Filter_city};
            case Tab.Country:
                return {key: this.Translation.Filter_country};
            case Tab.Date:
                return {key: this.Translation.Filter_date};
        }
    }

    public tabHeadSlotName(tabName: Tab) {
        return `a-tabHead-${tabName}`;
    }

    public tabPanelSlotName(tabName: Tab) {
        return `a-tabPanel-${tabName}`;
    }

    public tabPanelBtnSlotName(tabName: Tab) {
        return `a-tabPanelBtn-${tabName}`;
    }

    public getFilterName(category: string, id: string | number) {
        const langCode = this.lang === LanguageCode.English
            ? LanguageCode.English
            : 'lc';

        if (!this.data) {
            return;
        }

        switch (category) {
            case 'country':
                return this.data.countryTranslations[langCode][id.toString().toUpperCase()];
            case 'category':
                return this.data.categoryTranslations.category[langCode][id] !== undefined
                    ? this.data.categoryTranslations.category[langCode][id]
                    : this.data.categoryTranslations.mainCategory[langCode][5];
            case 'mainCategory':
                return this.data.categoryTranslations.mainCategory[langCode][id] !== undefined
                    ? this.data.categoryTranslations.mainCategory[langCode][id]
                    : this.data.categoryTranslations.mainCategory[langCode][5];
            default:
                return id;
        }
    }

    public handleFilterSelection(val: string | number, category: Tab) {
        this.tabPristine = false;

        const isChecked: boolean = this.isValueIncludedIn(category, val);
        const currentFilterValues = this.selectedFilters.value[category === Tab.MainCategory ? Tab.MainCategory : this.activeTab] as (string | number)[];

        if (!isChecked) {
            currentFilterValues.push(val);
        } else {
            currentFilterValues.includes(val) ?
                currentFilterValues.splice(currentFilterValues.indexOf(val), 1)
                : null;
        }
    }

    public async clearFilter(category: string, filterValue: string | number) {
        const filter = this.selectedFilters.value[category];
        if (!filter) {
            return;
        }

        filter.splice(filter.indexOf(filterValue), 1);

        await this.appliedFiltersStore.deleteAppliedFilters({[category]: filterValue});
        this.searchResultsStore.getData(false, this.$apiService);
    }

    public toggleDatepickers(which: string) {
        this.hideDatepickerStart = which === 'end';
        this.switchCalendar(which);
    }

    public isValueIncludedIn(activeFilter: string, value: string | number) {
        if (!this.selectedFilters.value[activeFilter]) {
            return false;
        }

        return this.selectedFilters.value[activeFilter].includes(value);
    }

    public updateSelectedFilters() {
        this.selectedFilters.value = this.mapToSelectedFilters();
    }

    public handleActiveTabChange(tab: Tab) {
        this.activeTab = tab;

        if (tab === Tab.Date) {
            this.mountCalendar();
        }
    }

    private setupCalendarValueHandling() {
        window.addEventListener('calendar', (e) => {
            if (e['detail'].type === 'start') {
                this.selectedDates.start = e['detail'] && e['detail'].timestamp ? new Date(e['detail'].timestamp) : null;
                this.updateDateStart();
            } else {
                this.selectedDates.end = e['detail'] && e['detail'].timestamp ? new Date(e['detail'].timestamp) : null;
                this.updateDateEnd();
            }
        });
    }

    /** Breakpoint change handler method */
    protected handleBreakpointChange(): void {
        this.rerenderOnBreakpointChange();
    }

    private rerenderOnBreakpointChange() {
        this.modalKey += 1;
    }

    /**
     * Created lifecycle hook
     */
    private created() {
        // needed for CSS to have filters stick to bottom on mobile
        FilterComponent.setRootPropertyVh();
        // also listen to the resize event
        window.addEventListener('resize', () => {
            FilterComponent.setRootPropertyVh();
        });

        this.selectedFilters.value = this.mapToSelectedFilters();
    }

    private minDateForEnd() {
        const minDate = this.filters && this.filters.minDate !== undefined ? new Date((this.filters.minDate as unknown as DateTimeZone).dateTime) : new Date();
        const fromDate = this.selectedDates.start !== null ? this.selectedDates.start : new Date();

        if (minDate === fromDate) {
            return minDate;
        } else {
            return fromDate;
        }
    }

    private maxDateForStart() {
        const toDate = this.selectedDates.end !== null ? this.selectedDates.end : null;

        return toDate;
    }

    private mountCalendar(unmountFirst = false) {
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('mountCalendar',
                {
                    detail: {
                        maxDateForStart: this.maxDateForStart(),
                        minDateForEnd: this.minDateForEnd(),
                        startDate: this.dateStartForPicker,
                        endDate: this.dateEndForPicker,
                        minDateForStart: this.filters && this.filters.minDate !== undefined ?
                            new Date((this.filters.minDate as unknown as DateTimeZone).dateTime)
                            : new Date(),
                        shouldUnmount: unmountFirst,
                    }
                }));
        }, 0);
    }

    private mapToSelectedFilters(): CheckboxFilters {
        return {
            country: this.appliedFiltersStore.country ?? [],
            city: this.appliedFiltersStore.city ?? [],
            category: this.appliedFiltersStore.category ?? [],
            mainCategory: this.appliedFiltersStore.mainCategory ?? [],
        }
    }

    private switchCalendar(startOrEnd: string) {
        window.dispatchEvent(new CustomEvent('calendarSwitch', {
            detail: {
                type: startOrEnd,
                startValue: startOrEnd === 'start' ? this.selectedDates.start : this.selectedDates.end,
            }
        }));
    }

    private static setRootPropertyVh() {
        let vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
}
</script>

<template>
    <div id="filterTrigger">
        <button type="button"
                class="a-btnCta -action"
                @click="showModal()">
            <icon-component :icon-id="IconName.Filter" class="a-btnIcon"/>
            <span v-translate="{ key: Translation.Filter_trigger }"></span>
        </button>

        <ModalComponent
            v-if="filters"
            v-show="isModalVisible"
            :hasHeader="false"
            :isStickyOnMobile="true"
            :key="modalKey"
            @closeModal="closeModal"
        >
            <template v-slot:body>
                <TabsComponent
                    v-bind:tabs="getTabConfiguration()"
                    v-bind:initialTab="initialTab"
                    v-bind:modifier="modifier"
                    @closeAndSave="closeAndSave"
                    @tabSwitched="tabSwitched"
                    @activeTabChange="handleActiveTabChange">
                    <template v-if="isMobile" v-slot:[tabHeadSlotName(modifier)]></template>
                    <template v-if="!isMobile" v-slot:[modifier]>
                        <template v-for="(filter, filterIndex) in selectedFilters.value">
                            <template v-for="filterValue in filter" :key="filterValue">
                                <button @click="clearFilter(filterIndex, filterValue)"
                                        class="a-btnSmall -secondary -filtered">
                                    <icon-component :icon-id="IconName.Close" class="a-btnIcon"/>
                                    <span>{{ getFilterName(filterIndex, filterValue) }}</span>
                                </button>
                            </template>
                        </template>
                        <template v-if="dateStart || dateEnd">
                            <button @click="clearDates" class="a-btnSmall -secondary -filtered">
                                <icon-component :icon-id="IconName.Close" class="a-btnIcon"/>
                                <span>{{ dateStart }} - {{ dateEnd }}</span>
                            </button>
                        </template>
                    </template>
                    <template v-if="isMobile" v-slot:[tabPanelSlotName(modifier)]>
                        <span v-translate="{ key: Translation.Filter_by}" class="a-label"></span>
                        <div>
                            <template v-for="(filter, filterIndex) in selectedFilters.value">
                                <template v-for="filterValue in filter" :key="filterValue">
                                    <button @click="clearFilter(filterIndex, filterValue)"
                                            class="a-btnSmall -secondary -filtered">
                                        <icon-component :icon-id="IconName.Close" class="a-btnIcon"/>
                                        <span>{{ getFilterName(filterIndex, filterValue) }}</span>
                                    </button>
                                </template>
                            </template>
                            <template v-if="dateStart || dateEnd">
                                <button @click="clearDates" class="a-btnSmall -secondary -filtered">
                                    <icon-component :icon-id="IconName.Close" class="a-btnIcon"/>
                                    <span>{{ dateStart }} - {{ dateEnd }}</span>
                                </button>
                            </template>
                        </div>
                    </template>
                    <template v-if="isMobile" v-slot:[tabPanelBtnSlotName(modifier)]>
                        <button
                            type="button"
                            class="a-btnCta -action"
                            aria-label="Close modal"
                            v-on:click="closeAndSave"
                        >
                            <span v-translate="{ key: Translation.Search_submit}"></span>
                        </button>
                    </template>
                    <template v-slot:[tabHeadSlotName(activeTab)]>
                        <span v-translate="translationForTabName(activeTab)"></span>
                        <icon-component :icon-id="IconName.Chevron" class="a-tabHead__icon"/>
                    </template>
                    <template v-slot:[tabPanelSlotName(activeTab)]>
                        <template v-if="activeTab === Tab.Date">
                            <div class="o-datePicker">
                                <div class="o-datePicker__dates">
                                    <label>
                                        <span v-translate="{ key: Translation.Date_from_label}" class="a-label">
                                            From</span>
                                        <div :class="['a-formField', {'active' : !hideDatepickerStart}]">
                                            <input v-model="dateStart"
                                                   @focus="toggleDatepickers('start')"
                                                   :readonly="isMobile"
                                                   type="text"
                                                   class="a-formInput">
                                            <div v-show="dateStart !== null && dateStart !== ''"
                                                 @click="clearDateStart"
                                                 class="a-formField__action">
                                                <icon-component :icon-id="IconName.Close">Close</icon-component>
                                            </div>
                                        </div>
                                    </label>
                                    <label>
                                        <span v-translate="{ key: Translation.Date_to_label}"  class="a-label">To</span>
                                        <div :class="['a-formField', {'active' : !hideDatepickerEnd}]">
                                            <input  v-model="dateEnd"
                                                    @focus="toggleDatepickers('end')"
                                                    :readonly="isMobile"
                                                    type="text"
                                                    class="a-formInput">
                                            <div v-show="dateEnd !== null && dateEnd !== ''"
                                                 @click="clearDateEnd"
                                                 class="a-formField__action">
                                                <icon-component :icon-id="IconName.Close">Close</icon-component>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                                <div class="o-datePicker__calendar">
                                    <div id="calendar-app"></div>
                                </div>
                            </div>
                        </template>
                        <template v-else-if="activeTab === Tab.Country">
                            <ul class="a-filterList">
                                <li v-for="val in allFilters[activeTab]" :key="val" class="a-filterList__item">
                                    <label>
                                        <icon-component :icon-id="checkboxIcon(isValueIncludedIn(activeTab, val))"
                                                        class="a-btnIcon"/>
                                        <input :name=val
                                               :value=val
                                               :checked="isValueIncludedIn(activeTab, val)"
                                               @change="handleFilterSelection(val, activeTab)"
                                               type="checkbox">
                                        <span>{{ getFilterName(activeTab, val) }}</span>
                                    </label>
                                </li>
                            </ul>

                            <div v-if="allFilters[activeTab].length === 0" class="m-blockNotice">
                                <span v-translate="{ key: Translation.No_filters_notice }"></span>
                            </div>
                        </template>
                        <template v-else-if="activeTab === Tab.Category">
                            <ul class="a-filterList">
                                <li v-for="val in allFilters[Tab.MainCategory]" :key="val" class="a-filterList__item">
                                    <label>
                                        <icon-component :icon-id="checkboxIcon(isValueIncludedIn(Tab.MainCategory, val))"
                                                        class="a-btnIcon"/>
                                        <input type="checkbox"
                                               :name=val
                                               :value=val
                                               :checked="isValueIncludedIn(Tab.MainCategory, val)"
                                               @change="handleFilterSelection(val, Tab.MainCategory)">
                                        <span>{{ getFilterName(Tab.MainCategory, val) }}</span>
                                    </label>
                                </li>
                                <li v-for="val in allFilters[Tab.Category]" :key="val" class="a-filterList__item">
                                    <label>
                                        <icon-component :icon-id="checkboxIcon(isValueIncludedIn(Tab.Category, val))"
                                                        class="a-btnIcon"/>
                                        <input type="checkbox"
                                               :name=val
                                               :value=val
                                               :checked="isValueIncludedIn(Tab.Category, val)"
                                               @change="handleFilterSelection(val, activeTab)">
                                        <span>{{ getFilterName(Tab.Category, val) }}</span>
                                    </label>
                                </li>
                            </ul>
                            <div v-if="allFilters[activeTab].length === 0 && allFilters['mainCategory'].length === 0"
                                 class="m-blockNotice">
                                <span v-translate="{ key: Translation.No_filters_notice }"></span>
                            </div>
                        </template>
                        <template v-else-if="activeTab === Tab.City">
                            <div class="m-blockNotice" v-if="allFilters[activeTab].length === 0">
                                <span v-translate="{ key: Translation.No_filters_notice }"></span>
                            </div>
                            <template v-else>
                                <ul class="a-filterList -has-dropdown">
                                    <li v-for="val in allFilters[activeTab]" :key="val" class="a-filterList__item">
                                        <label>
                                            <icon-component :icon-id="checkboxIcon(isValueIncludedIn(activeTab, val))"
                                                            class="a-btnIcon"/>
                                            <input type="checkbox"
                                                   :name=val
                                                   :value=val
                                                   :checked="isValueIncludedIn(activeTab, val)"
                                                   @change="handleFilterSelection(val, activeTab)">
                                            <span>{{ val }}</span>
                                        </label>
                                    </li>
                                </ul>
                            </template>
                        </template>
                    </template>
                </TabsComponent>
            </template>
            <template v-slot:footer>
                <SpinnerComponent
                    v-show="isLoading"
                    class="-inline"
                    label="">
                </SpinnerComponent>
                <button
                    type="button"
                    @click="closeAndSave"
                    class="a-btnCta -action"
                    aria-label="Close modal"
                >
                    <span v-translate="{ key: Translation.Search_submit }"></span>
                </button>
            </template>
        </ModalComponent>
    </div>
</template>

<style scoped>
#filterTrigger > .a-btnCta span {
    display: none;
}

#filterTrigger > .a-btnCta .a-btnIcon {
    margin: 0;
}

@media (min-width: 640px) {
    #filterTrigger .a-btnCta span {
        display: inherit;
    }

    #filterTrigger > .a-btnCta .a-btnIcon {
        margin-right: 15px;
    }
}
</style>
